import React, { useState, useEffect } from 'react';
import { formatPhoneNumber } from '../../../utils/utils';

interface DonorInformationProps {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  specialDesignation?: string;
  onFieldChange: (field: string, value: string) => void;
  errors: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onValidationChange?: (isValid: boolean) => void;
  title?: string;
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const DonorInformation: React.FC<DonorInformationProps> = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  streetAddress,
  onFieldChange,
  errors,
  onValidationChange,
  title = 'General Donation'
}) => {
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    isTouched: false,
    errors: [] as string[]
  });

  const validateEmail = (emailValue: string): string[] => {
    const validationErrors: string[] = [];
    if (!emailValue.trim()) {
      validationErrors.push('Email is required');
      return validationErrors;
    }
    if (!EMAIL_REGEX.test(emailValue)) {
      validationErrors.push('Please enter a valid email address');
      return validationErrors;
    }
    if (emailValue.length > 254) {
      validationErrors.push('Email is too long');
    }
    const localPart = emailValue.split('@')[0];
    if (localPart.length > 64) {
      validationErrors.push('Email username is too long');
    }
    if (emailValue.includes('..')) {
      validationErrors.push('Email cannot contain consecutive dots');
    }
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      validationErrors.push('Email username cannot start or end with a dot');
    }
    if (emailValue.includes(' ')) {
      validationErrors.push('Email cannot contain spaces');
    }
    const domain = emailValue.split('@')[1]?.toLowerCase();
    const tld = domain?.split('.').pop();
    if (tld && tld.length < 2) {
      validationErrors.push('Email domain appears to be invalid');
    }
    return validationErrors;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    onFieldChange('email', emailValue);
    if (emailValidation.isTouched || emailValue.length > 0) {
      const emailErrors = validateEmail(emailValue);
      setEmailValidation({
        isValid: emailErrors.length === 0,
        isTouched: true,
        errors: emailErrors
      });
    }
  };

  const handleEmailBlur = () => {
    const emailErrors = validateEmail(email);
    setEmailValidation({
      isValid: emailErrors.length === 0,
      isTouched: true,
      errors: emailErrors
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    onFieldChange('phoneNumber', formattedValue);
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(emailValidation.isValid);
    }
  }, [emailValidation.isValid, onValidationChange]);

  const inputClasses = (error?: string) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#71001F] transition-all text-gray-700 ${
      error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
    }`;

  return (
    <div id="donate-form" className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100 w-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-[#71001F] rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">All fields marked with * are required</p>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
        {/* First Name / Last Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => onFieldChange('firstName', e.target.value)}
              className={inputClasses(errors.firstName)}
              placeholder="First Name"
            />
            {errors.firstName && <span className="text-red-500 text-xs mt-1 block">{errors.firstName}</span>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => onFieldChange('lastName', e.target.value)}
              className={inputClasses(errors.lastName)}
              placeholder="Last Name"
            />
            {errors.lastName && <span className="text-red-500 text-xs mt-1 block">{errors.lastName}</span>}
          </div>
        </div>

        {/* Email / Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email *
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                className={inputClasses(emailValidation.isTouched && emailValidation.errors.length > 0 ? 'error' : undefined)}
                placeholder="email@example.com"
              />
              {emailValidation.isTouched && email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {emailValidation.isValid ? (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
            </div>
            {emailValidation.isTouched && emailValidation.errors.length > 0 && (
              <span className="text-red-500 text-xs mt-1 block">{emailValidation.errors[0]}</span>
            )}
          </div>
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone Number *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">+1</span>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneChange}
                className={`${inputClasses(errors.phoneNumber)} pl-10`}
                placeholder="(555) 123-4567"
                maxLength={12}
              />
            </div>
            {errors.phoneNumber && <span className="text-red-500 text-xs mt-1 block">{errors.phoneNumber}</span>}
          </div>
        </div>

        {/* Street Address */}
        <div>
          <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1.5">
            Street Address *
          </label>
          <input
            type="text"
            id="streetAddress"
            value={streetAddress}
            onChange={(e) => onFieldChange('streetAddress', e.target.value)}
            className={inputClasses(errors.streetAddress)}
            placeholder="123 Main Street"
          />
          {errors.streetAddress && <span className="text-red-500 text-xs mt-1 block">{errors.streetAddress}</span>}
        </div>
      </form>
    </div>
  );
};

export default DonorInformation;
